import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Save as SaveIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Email as EmailIcon,
  Schedule as ScheduleIcon,
  PlayArrow as TestIcon
} from '@mui/icons-material';
import { dailyReportService, DailyReportConfig, TestReportResult } from '../services/dailyReportService';

const DailyReportSettings: React.FC = () => {
  const [config, setConfig] = useState<DailyReportConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testResults, setTestResults] = useState<any>(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const data = await dailyReportService.getConfig();
      setConfig(data);
      showSnackbar('Configuration loaded successfully', 'success');
    } catch (error) {
      console.error('Failed to load configuration:', error);
      showSnackbar('Failed to load configuration', 'error');
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    if (!config) return;

    // Client-side validation
    const validationErrors = dailyReportService.validateConfig(config);
    if (validationErrors.length > 0) {
      showSnackbar(`Validation errors: ${validationErrors.join(', ')}`, 'error');
      return;
    }

    setSaving(true);
    try {
      await dailyReportService.saveConfig(config);
      showSnackbar('Configuration saved successfully', 'success');

      // Reload config to get updated timestamps
      await loadConfig();
    } catch (error: any) {
      console.error('Failed to save configuration:', error);
      showSnackbar(error.message || 'Failed to save configuration', 'error');
    } finally {
      setSaving(false);
    }
  };

  const testReport = async () => {
    setTesting(true);
    setTestResults(null);

    try {
      const data = await dailyReportService.testReport();
      setTestResults(data);
      setTestDialogOpen(true);

      const successful = data.emailResults?.filter((r: any) => r.success).length || 0;
      const total = data.emailResults?.length || 0;

      showSnackbar(`Test sent to ${successful}/${total} recipients`, successful > 0 ? 'success' : 'warning');
    } catch (error: any) {
      console.error('Failed to send test report:', error);
      showSnackbar(error.message || 'Failed to send test report', 'error');
    } finally {
      setTesting(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleRecipientAdd = (email: string) => {
    if (!config || !email.trim()) return;

    const trimmedEmail = email.trim().toLowerCase();
    if (config.recipients.includes(trimmedEmail)) {
      showSnackbar('Email already exists in recipients list', 'warning');
      return;
    }

    setConfig({
      ...config,
      recipients: [...config.recipients, trimmedEmail]
    });
  };

  const handleRecipientRemove = (emailToRemove: string) => {
    if (!config) return;

    setConfig({
      ...config,
      recipients: config.recipients.filter(email => email !== emailToRemove)
    });
  };

  const handleRecipientKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const input = event.target as HTMLInputElement;
      handleRecipientAdd(input.value);
      input.value = '';
    }
  };

  if (!config) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading configuration...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
        <SettingsIcon sx={{ mr: 2 }} />
        Daily Report Settings
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Configure automated daily report scheduling and email recipients using Firebase Functions
      </Typography>

      <Grid container spacing={3}>
        {/* Main Configuration Card */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardHeader
              title="Report Configuration"
              avatar={<ScheduleIcon />}
            />
            <CardContent>
              <Grid container spacing={3}>
                {/* Schedule Time */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Schedule Time (NZT)"
                    type="time"
                    value={config.scheduleTime}
                    onChange={(e) => setConfig({ ...config, scheduleTime: e.target.value })}
                    helperText="Time when daily reports are sent (New Zealand Time)"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                {/* Timezone */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Timezone</InputLabel>
                    <Select
                      value={config.timezone}
                      label="Timezone"
                      onChange={(e) => setConfig({ ...config, timezone: e.target.value })}
                    >
                      <MenuItem value="Pacific/Auckland">Pacific/Auckland (NZT)</MenuItem>
                      <MenuItem value="UTC">UTC</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Enable/Disable */}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.enabled}
                        onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
                        color="primary"
                      />
                    }
                    label="Enable Daily Reports"
                  />
                </Grid>

                {/* Recipients */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Email Recipients
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Add email addresses that will receive the daily reports
                  </Typography>

                  {/* Add new recipient */}
                  <Box sx={{ mb: 2 }}>
                    <TextField
                      fullWidth
                      label="Add Email Recipient"
                      placeholder="Enter email address and press Enter"
                      onKeyPress={handleRecipientKeyPress}
                      helperText="Press Enter to add the email address"
                    />
                  </Box>

                  {/* Recipients chips */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {config.recipients.map((email) => (
                      <Chip
                        key={email}
                        label={email}
                        onDelete={() => handleRecipientRemove(email)}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>

                  {config.recipients.length === 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      No recipients configured. Add at least one email address to receive reports.
                    </Alert>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>


        {/* Status Information Card */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Status Information"
              avatar={<ScheduleIcon />}
            />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Last Sent
                  </Typography>
                  <Typography variant="body1">
                    {config.lastSent ? new Date(config.lastSent).toLocaleString() : 'Never'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Next Scheduled
                  </Typography>
                  <Typography variant="body1">
                    {config.nextScheduled ? new Date(config.nextScheduled).toLocaleString() : 'Not scheduled'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Firebase Scheduler Status
                  </Typography>
                  <Typography variant="body1">
                    {config.cronJobId || 'Not configured'}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Firebase Functions Scheduling:</strong> Reports are sent automatically using Firebase Functions.
                  The scheduler runs every 15 minutes and sends reports at the configured time.
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Box sx={{ mt: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={saveConfig}
          disabled={saving}
          sx={{ minWidth: 120 }}
        >
          {saving ? <CircularProgress size={20} /> : 'Save Configuration'}
        </Button>

        <Button
          variant="outlined"
          startIcon={<TestIcon />}
          onClick={testReport}
          disabled={testing || !config.enabled}
          sx={{ minWidth: 120 }}
        >
          {testing ? <CircularProgress size={20} /> : 'Send Test Report'}
        </Button>

        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadConfig}
          disabled={loading}
          sx={{ minWidth: 120 }}
        >
          {loading ? <CircularProgress size={20} /> : 'Refresh'}
        </Button>
      </Box>

      {/* Test Results Dialog */}
      <Dialog
        open={testDialogOpen}
        onClose={() => setTestDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Test Report Results</DialogTitle>
        <DialogContent>
          {testResults && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Test Summary
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Successfully sent to {testResults.emailResults?.filter((r: any) => r.success).length || 0} of {testResults.emailResults?.length || 0} recipients
              </Typography>

              <Typography variant="h6" gutterBottom>
                Detailed Results
              </Typography>
              {testResults.emailResults?.map((result: any, index: number) => (
                <Box key={index} sx={{ mb: 1, p: 1, border: 1, borderColor: result.success ? 'success.main' : 'error.main', borderRadius: 1 }}>
                  <Typography variant="body2">
                    <strong>{result.recipient}:</strong> {result.success ? '✅ Success' : '❌ Failed'}
                  </Typography>
                  {result.error && (
                    <Typography variant="body2" color="error">
                      Error: {result.error}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DailyReportSettings;
